// src/admin/screens/loans/tabs/ScheduleTab.tsx
// Repayment schedule (installments) for the loan. The tab introduces itself
// with a SectionHead summarising scheduled vs paid, then renders the rows in
// a DataTable. Status tones map to the loan_installment_status enum.
import DataTable, { type Column } from '@admin/components/DataTable';
import StatusChip from '@admin/components/StatusChip';
import Money from '@admin/components/Money';
import SectionHead from '@admin/components/SectionHead';
import { formatCatDate } from '@admin/utils/dates';
import { formatKwacha } from '@admin/utils/money';
import type { Ngwee } from '@/lib/types';
import type { Database } from '@/lib/database.types';
import type { LoanDetail } from '@admin/hooks/useLoan';

type Row = LoanDetail['loan_repayment_schedule'][number];
type ScheduleStatus = Database['public']['Enums']['loan_installment_status'];

const LABEL: Record<ScheduleStatus, string> = {
  pending: 'Scheduled',
  paid: 'Paid',
  partial: 'Partial',
  overdue: 'Overdue',
};
const TONE: Record<ScheduleStatus, 'info' | 'positive' | 'warn' | 'danger'> = {
  pending: 'info',
  paid: 'positive',
  partial: 'warn',
  overdue: 'danger',
};

export default function ScheduleTab({ loan }: { loan: LoanDetail }) {
  const rows = [...loan.loan_repayment_schedule].sort((a, b) => a.installment_no - b.installment_no);
  const paidCount = rows.filter((r) => r.status === 'paid').length;
  const overdueCount = rows.filter((r) => r.status === 'overdue').length;
  const totalScheduled = rows.reduce((s, r) => s + Number(r.scheduled_amount_ngwee ?? 0), 0);
  const totalPaid = rows.reduce((s, r) => s + Number(r.paid_amount_ngwee ?? 0), 0);

  const description = rows.length === 0
    ? 'No installments scheduled yet.'
    : `${paidCount} of ${rows.length} installment${rows.length === 1 ? '' : 's'} paid · ${formatKwacha(totalPaid as Ngwee, { decimals: 0 })} of ${formatKwacha(totalScheduled as Ngwee, { decimals: 0 })}${overdueCount ? ` · ${overdueCount} overdue` : ''}`;

  const cols: Column<Row>[] = [
    { key: 'no', header: '#', render: (r) => r.installment_no, width: '60px' },
    { key: 'due', header: 'Due', render: (r) => formatCatDate(r.due_date) },
    { key: 'sch', header: 'Scheduled', render: (r) => <Money amount={r.scheduled_amount_ngwee as Ngwee} />, align: 'right' },
    { key: 'paid', header: 'Paid', render: (r) => <Money amount={r.paid_amount_ngwee as Ngwee} />, align: 'right' },
    { key: 'st', header: 'Status', render: (r) => <StatusChip label={LABEL[r.status]} tone={TONE[r.status]} /> },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <SectionHead
        eyebrow="Repayment"
        title="Schedule"
        description={description}
      />
      <DataTable
        rows={rows}
        columns={cols}
        rowKey={(r) => r.id}
        emptyLabel="No schedule yet."
      />
    </div>
  );
}
